import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
    convertDate,
    createDateTimeObjectFromDate,
    getGetHeaders, getLocalTimeZoneName
} from '../shared/functions';
import {setDefaultTableSort, filterCaseInsensitive, getTableState} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import ReactTable from 'react-table';
import {RESOURCES as searchResources} from '../fuseSearchResourceMap';
import EditButtonByCustomKey from '../buttons/EditButtonByCustomKey';
import ProductUnitsStaticIconButton from '../buttons/ProductUnitsStaticIconButton';
import TransferListProductsTable from './TransferListProductsTable';
import ViewButtonByCustomKey from '../buttons/ViewButtonByCustomKey';
import {tableStyles} from '../shared/styles-table';
import '../shared/react-table.css';

class CollectionTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dataComplete: false,
            dataList: [],
            dataTable: [],
            message: null
        };
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchData();
    }

    componentWillReceiveProps(nextProps) {
        const nextFilter = JSON.stringify(nextProps.filterValues);
        const thisFilter = JSON.stringify(this.props.filterValues);
        if (
            nextFilter !== thisFilter ||
            (!nextProps.listComplete && this.props.listComplete)
        ) {
            this.setState({
                dataComplete: false,
                dataList: [],
                dataTable: [],
                message: null
            });
        }
    }

    componentDidUpdate(prevProps) {
        const prevFilter = JSON.stringify(prevProps.filterValues);
        const thisFilter = JSON.stringify(this.props.filterValues);
        if (
            prevFilter !== thisFilter ||
            (prevProps.listComplete && !this.props.listComplete)
        ) {
            this.fetchData();
        }
    }

    /**
     * Get Repacks and/or Retail Boxes data containing optional ID or Product term
     *
     */
    fetchData() {
        const filterValues = this.props.filterValues;
        let params = {
            filter: {
                allow_search: filterValues.allowSearch,
                collection_type: filterValues.collectionType,
                date_from: filterValues.dateFrom,
                date_to: filterValues.dateTo,
                condition: filterValues.receivedCondition,
                search_id: filterValues.searchId,
                search_name: filterValues.searchName
            },
        };
        let collectionStr = 'Repacks or Retail Boxes';
        let noneMessage;
        if (filterValues.collectionType === 'repack') {
            collectionStr = 'Repacks';
            noneMessage = 'No ' + collectionStr + ' found within this date range';
        } else if (filterValues.collectionType === 'retail_box') {
            collectionStr = 'Retail Boxes';
            noneMessage = 'No ' + collectionStr + ' found within this date range';
        } else {
            if (filterValues.searchId && filterValues.searchId !== '') {
                noneMessage = 'No ' + collectionStr + ' containing ID "' + filterValues.searchId + '" within this date range';
            }
            if (filterValues.searchName && filterValues.searchName !== '') {
                noneMessage = 'No ' + collectionStr + ' containing Product "' + filterValues.searchName + '" within this date range';
            }
        }
        let resource = searchResources.RepacksRetailBoxes.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            let list = [];
            if (result.error) {
                noneMessage = 'Sorry, there was an error retrieving ' + collectionStr + '.';
            } else if (result.data && result.data.payload) {
                list = result.data.payload;
            } else if (result.data && result.data.length > 0) {
                list = result.data;
            }
            this.processSearchData(list, noneMessage);
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
            } else {
            }
            this.processSearchData([], 'Sorry, there was an error retrieving ' + collectionStr + '.');
        });
    }

    /**
     * Process array of Repacks or Retail Boxes
     *
     * @param data
     * @param noneMessage
     */
    processSearchData(data, noneMessage) {
        let processedData = [];
        const tzName = getLocalTimeZoneName();
        for (let i = 0; i < data.length; i++) {
            let item = data[i];
            let modified = convertDate(item.modified);
            let modifiedDate = createDateTimeObjectFromDate(item.modified);

            // Create a Repack or Retail Box row
            let result = {
                id: item.collection_id,
                collection_id: item.collection_id,
                collection_type: item.collection_type,
                contents: item.contents,
                modified: item.modified,
                modified_date: modified + ' ' + tzName,
                modified_date_date: modifiedDate
            };
            processedData.push(result);
        }
        let message = (processedData.length === 0) ? noneMessage : null;
        this.storeProcessedData(processedData, message);
    }

    /**
     * Store data from processing and lift state
     *
     * @param data
     * @param message
     */
    storeProcessedData(data, message = null) {
        this.setState({
            dataComplete: true,
            dataList: data,
            dataTable: data,
            message: message
        }, () => {
            if (typeof this.props.onDataComplete === 'function') {
                this.props.onDataComplete(true);
            }
        });
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                dataTable: tableData
            });
        }
    }

    /**
     * Render a table subcomponent with a result's Products
     *
     * @param row
     * @returns {JSX.Element}
     */
    listSubcomponent = (row) => {
        let list = (row.original.contents && row.original.contents.length > 0) ? row.original.contents : [];
        let type = row.original.collection_type;
        let fv = this.props.filterValues;
        let searchTerm = (fv && fv.searchName) ? fv.searchName : '';

        return (
            <TransferListProductsTable
                type={type}
                list={list}
                searchTerm={searchTerm}
            />
        );
    };

    render() {
        const {
            basePathCollections,
            filterValues
        } = this.props;

        const {
            dataComplete,
            dataList,
            message
        } = this.state;

        const defaultSort = setDefaultTableSort({field: 'modified', order: 'ASC'});

        const tableColumns = [
            {
                Header: '',
                accessor: 'modified_date',
                style: tableStyles.hide,
                headerStyle: tableStyles.hide
            },
            {
                Header: 'Modified',
                accessor: 'modified_date_date',
                Cell: row => (
                    <span>{row.original.modified_date}</span>
                ),
                headerStyle: tableStyles.left
            },
            {
                Header: 'Collection ID',
                accessor: 'collection_id',
                headerStyle: tableStyles.left
            }
        ];
        if (filterValues.collection_type === 'any') {
            tableColumns.push(
                {
                    Header: 'Type',
                    accessor: 'collection_type',
                    width: 100,
                    headerStyle: tableStyles.left
                }
            );
        }
        tableColumns.push(
            {
                Header: 'Products',
                source: '',
                label: '',
                Expander: ({isExpanded}) =>
                    <div>
                        {isExpanded
                            ? <ProductUnitsStaticIconButton/>
                            : <ProductUnitsStaticIconButton/>}
                    </div>,
                expander: true,
                filterable: false,
                sortable: false,
                width: 90
            },
            {
                Header: '',
                accessor: 'id',
                filterable: false,
                sortable: false,
                Cell: row => (
                    <ViewButtonByCustomKey
                        basePath={basePathCollections}
                        customKey={(filterValues.collectionType === 'repack') ? 'repack_id' : 'retailbox_id'}
                        record={{
                            repack_id: row.original.collection_id,
                            retailbox_id: row.original.collection_id
                        }}
                    />
                ),
                width: 80,
                styles: {...tableStyles.right, ...tableStyles.noPadding}
            },
            {
                Header: '',
                accessor: 'id',
                filterable: false,
                sortable: false,
                Cell: row => (
                    <EditButtonByCustomKey
                        basePath={basePathCollections}
                        customKey={(filterValues.collectionType === 'repack') ? 'repack_id' : 'retailbox_id'}
                        record={{
                            repack_id: row.original.collection_id,
                            retailbox_id: row.original.collection_id
                        }}
                    />
                ),
                width: 80,
                styles: {...tableStyles.right, ...tableStyles.noPadding}
            }
        );

        return (
            <div>
                {dataComplete && dataList.length > 0 &&
                    <div className="list-table-container">
                        <ReactTable
                            key="table-collections-list"
                            data={dataList}
                            columns={tableColumns}
                            defaultPageSize={20}
                            showPagination={true}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.collectionsReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.collectionsReactTable)}
                            ref={(r) => this.collectionsReactTable = r}
                            resizable={false}
                            minRows={1}
                            SubComponent={this.listSubcomponent}
                        />
                    </div>
                }
                {dataComplete && message &&
                    <p>{message}</p>
                }
            </div>
        );
    }
}

CollectionTable.propTypes = {
    basePathCollections: PropTypes.string,
    filterValues: PropTypes.object,
    listComplete: PropTypes.bool,
    onDataComplete: PropTypes.func,
    system: PropTypes.string,
};

CollectionTable.defaultProps = {
    filterValues: {
        receivedCondition: '',
        searchName: '',
        startDate: null,
        startDateStr: '',
        transferType: ''
    },
    listComplete: false
};

export default CollectionTable;



// WEBPACK FOOTER //
// ./src/Fuse/components/CollectionTable.js